import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
	disconnectFromSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { useLocale } from '@gov-nx/core/service';
import { PageCode } from '@gov-nx/module/page';
import { invalidateMySubmissionsListQuery } from './queries';

export interface DisconnectFromAnotherSubmissionMutation {
	isDisconnecting: boolean;
	onDisconnectSubmit: () => void;
	onDisconnectCancel: () => void;
	isConfirmOpen: boolean;
	disconnect: (variables: DisconnectFromSubmissionMutationVariables) => void;
}

export interface DisconnectFromSubmissionMutationVariables {
	submissionId: number;
	datovaSchrankaId: string;
	datovaZpravaId: number;
}

export const useDisconnectFromSubmissionMutation = (
	onDisconnected?: () => void
): DisconnectFromAnotherSubmissionMutation => {
	const queryClient = useQueryClient();
	const { toastMessageSuccess } = useMessageEvents();
	const { t } = useLocale(PageCode['moje-podani']);
	const { axiosInstance } = useAxiosInstance();

	const [variables, setVariables] =
		useState<DisconnectFromSubmissionMutationVariables | null>(null);

	const mutation = usePoMutation({
		mutationFn: async (): Promise<void> => {
			if (!variables) {
				return;
			}
			await disconnectFromSubmissionQuery(axiosInstance, {
				submissionId: variables.submissionId,
				body: {
					datovaSchrankaId: variables.datovaSchrankaId,
					datovaZpravaId: variables.datovaZpravaId,
				},
			});
		},
		onSuccess: async () => {
			toastMessageSuccess(t('formular.zprava.odpojeno'));
			await invalidateMySubmissionsListQuery(queryClient);
			setVariables(null);

			if (onDisconnected) {
				onDisconnected();
			}
		},
	});

	return {
		isDisconnecting: mutation.isLoading,
		onDisconnectSubmit: mutation.mutate,
		onDisconnectCancel: () => setVariables(null),
		isConfirmOpen: variables !== null,
		disconnect: setVariables,
	};
};
